import { ref } from 'vue'
import type { ConfigChange } from '@dashboard/composables/useConfigChanges'

interface ChangeRecord {
  signature: string
  changedAt: number | null
}

export const configChangeKey = (change: ConfigChange) => `${change.category}:${change.id}`

// Details repeat for every edit of the same field ("Title changed"), so the
// diff decides whether a modified item moved. Added items carry no diff, so
// only renames advance their time. Hashed to keep localStorage small.
function changeSignature(change: ConfigChange): string {
  const identity = change.type === 'added' ? change.name : null
  const text = JSON.stringify([change.type, identity, change.details ?? [], change.diff ?? null])
  let hash = 5381
  for (let index = 0; index < text.length; index += 1) {
    hash = Math.imul(hash, 33) ^ text.charCodeAt(index)
  }
  return (hash >>> 0).toString(36)
}

export function useConfigChangeTimes() {
  const records = ref<Record<string, ChangeRecord>>({})
  const analyzedTeams = new Set<string>()
  const sessionRecords = new Map<string, Record<string, ChangeRecord>>()

  function recordChanges(teamPid: string, changes: ConfigChange[]) {
    const key = `rm_config_change_times_${teamPid}`
    let stored: Record<string, ChangeRecord> = {}
    try {
      const parsed: unknown = JSON.parse(localStorage.getItem(key) || '{}')
      if (parsed && typeof parsed === 'object') {
        for (const [id, value] of Object.entries(parsed)) {
          if (
            value &&
            typeof value === 'object' &&
            'signature' in value &&
            typeof value.signature === 'string' &&
            'changedAt' in value &&
            (value.changedAt === null || (typeof value.changedAt === 'number' && Number.isFinite(value.changedAt)))
          ) {
            stored[id] = { signature: value.signature, changedAt: value.changedAt }
          }
        }
      }
    } catch {
      stored = sessionRecords.get(teamPid) ?? {}
    }
    const initial = !analyzedTeams.has(teamPid)
    const next: Record<string, ChangeRecord> = {}
    for (const change of changes) {
      const id = configChangeKey(change)
      const signature = changeSignature(change)
      const previous = stored[id]
      next[id] = {
        signature,
        changedAt: previous?.signature === signature ? previous.changedAt : initial ? null : Date.now()
      }
    }
    records.value = next
    sessionRecords.set(teamPid, next)
    analyzedTeams.add(teamPid)
    try {
      localStorage.setItem(key, JSON.stringify(next))
    } catch {
      // Keep timestamps available for this session when storage is unavailable.
    }
  }

  const changedAt = (change: ConfigChange) => records.value[configChangeKey(change)]?.changedAt ?? null
  return { recordChanges, changedAt }
}
